import PropTypes from 'prop-types';
import React from 'react';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import uniqBy from 'lodash/uniqBy';
import pick from 'lodash/pick';
import { withState, compose } from 'recompose';
import { ddp } from '@zedoc/ddp-connector';
import { createFilterOptionFactory } from '@zedoc/text';
import {
  PropTypesInput,
  PropTypesMeta,
  PropTypesOption,
} from '@zedoc/react-questionnaire';
import FormFieldWrapper from '../../common/components/FormFieldWrapper';
import Select from '../Select';
import {
  apiTerminologyExpandValueSet,
  apiTerminologyLookupValueSet,
} from '../../common/api/terminology';

const getFilterOption = createFilterOptionFactory();

const allowedCustomProps = [
  'allowClear',
  'autoFocus',
  'disabled',
  'dropdownClassName',
];

const toOptions = (result) =>
  map(
    result && result.expansion && result.expansion.contains,
    ({ code, display }) => ({
      value: code,
      label: display,
    }),
  );

class FormFieldSearch extends React.Component {
  // NOTE: We do it to prevent results dissapearing
  //       while the next query is evaluated.
  static getDerivedStateFromProps(props, state) {
    const { expandQueryResult, lookupQueryResult } = props;
    const newState = {
      ...state,
    };
    if (expandQueryResult && expandQueryResult !== state.expandQueryResult) {
      newState.expandQueryResult = expandQueryResult;
    }
    if (lookupQueryResult && lookupQueryResult !== state.lookupQueryResult) {
      newState.lookupQueryResult = lookupQueryResult;
    }
    return newState;
  }

  constructor(props) {
    super(props);
    this.state = {
      expandQueryResult: null,
      lookupQueryResult: null,
    };
    this.handleSearch = this.handleSearch.bind(this);
  }

  handleSearch(searchText) {
    const { setSearchText } = this.props;
    if (setSearchText) {
      setSearchText(searchText);
    }
  }

  render() {
    const {
      label,
      input,
      meta,
      required,
      tooltip,
      placeholder,
      prefixSearchOnly,
      mode,
      options,
      'data-testid': datatestid,
      disabled,
      allowClear,
      autoFocus,
      shouldWrapText,
      isValidNewOption,
      ...otherProps
    } = this.props;
    const { lookupQueryResult, expandQueryResult } = this.state;

    return (
      <FormFieldWrapper
        label={label}
        required={required}
        meta={meta}
        tooltip={tooltip}
        disabled={disabled}
      >
        <Select
          data-testid={datatestid}
          placeholder={placeholder}
          onSearch={this.handleSearch}
          onSelect={this.handleSelect}
          onClear={this.handleClear}
          onDeselect={this.handleDeselect}
          // Overwrite input's onChange
          onChange={this.handleChange}
          value={
            mode === 'multiple' || mode === 'tags'
              ? input.value || []
              : input.value
          }
          mode={mode}
          onInputChange={this.handleSearch}
          onChange={input.onChange}
          value={mode === 'multiple' ? input.value || [] : input.value}
          isMulti={mode === 'multiple' || mode === 'tags'}
          isDisabled={disabled}
          filterOption={getFilterOption({
            prefixSearchOnly,
          })}
          options={
            (options || []).some((opt) => Array.isArray(opt.options))
              ? options
              : uniqBy(
                  [
                    ...(options || []),
                    ...toOptions(lookupQueryResult),
                    ...toOptions(expandQueryResult),
                  ],
                  'value',
                )
          }
          showSearch
          allowClear={allowClear}
          autoFocus={autoFocus}
          shouldWrapText={shouldWrapText}
          isClearable={allowClear}
          isCreatable={mode === 'tags'}
          isValidNewOption={isValidNewOption}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...pick(otherProps, allowedCustomProps)}
        />
      </FormFieldWrapper>
    );
  }
}

FormFieldSearch.propTypes = {
  input: PropTypesInput,
  meta: PropTypesMeta,
  label: PropTypes.string,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  placeholder: PropTypes.string,
  prefixSearchOnly: PropTypes.bool,
  setSearchText: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  expandQueryResult: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  lookupQueryResult: PropTypes.any,
  mode: PropTypes.string,
  allowClear: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypesOption),
  'data-testid': PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  isValidNewOption: PropTypes.func,
  allowClear: PropTypes.bool,
  shouldWrapText: PropTypes.bool,
};

FormFieldSearch.defaultProps = {
  input: null,
  meta: null,
  label: null,
  required: false,
  tooltip: null,
  placeholder: null,
  prefixSearchOnly: false,
  allowClear: false,
  searchText: null,
  setSearchText: null,
  expandQueryResult: null,
  lookupQueryResult: null,
  mode: null,
  options: null,
  'data-testid': 'select',
  disabled: false,
  autoFocus: false,
  allowClear: false,
  shouldWrapText: false,
};

export default compose(
  withState('searchText', 'setSearchText', ''),
  ddp({
    queries: (
      state,
      { input, valueSetId, valueSetDomains, searchText, prefixSearchOnly },
    ) => ({
      expandQueryResult: valueSetId
        ? apiTerminologyExpandValueSet.withParams({
            searchText,
            prefixSearchOnly,
            id: valueSetId,
            domains: valueSetDomains,
          })
        : null,
      lookupQueryResult:
        valueSetId && input && !isEmpty(input.value)
          ? apiTerminologyLookupValueSet.withParams({
              id: valueSetId,
              codes: isArray(input.value) ? input.value : [input.value],
            })
          : null,
    }),
    renderLoader: null,
  }),
)(FormFieldSearch);
